<template>
  <div class="site-self-settings" :style="{ height: height + 'px' }">
    <div class="p-4 bg-white">
      <!-- Main content -->
      <div
        class="text-sm font-medium text-center text-gray-500 border-gray-200 dark:text-gray-400 dark:border-gray-700"
      >
        <div class="bg-white p-3 border border-blue-100 rounded-md">
          <ul class="flex flex-wrap -mb-px border-b border-blue-100">
            <li
              v-for="tab in settingsTabs"
              :key="tab.value"
              class="me-2"
              @click="selectedTab = tab.value"
            >
              <a
                href="#"
                :class="
                  selectedTab === tab.value
                    ? 'selected-tab-item'
                    : 'border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                "
                class="inline-block p-2 border-b-2"
              >
                {{ tab.name }}
              </a>
              <!-- Info Tooltip -->
              <CustomTooltip :text="tab.tooltip" placement="right">
                <i class="fa-solid fa-xs fa-circle-info text-gray-400 cursor-pointer"></i>
              </CustomTooltip>
            </li>
          </ul>

          <div class="overflow-auto">
            <!-- Component Settings -->
            <div v-if="selectedTab === 'component_settings'">
              <ComponentSettings
                :key="currentComponent._id"
                :isEditable="isEditable"
                :currentComponent="currentComponent"
                :isComponentView="isComponentView"
                :settings="{
                  is_global: currentComponent.is_global,
                  name: currentComponent.name,
                  title: versionInfo.versionModel?.title
                }"
                @settings:change="setSettings"
              />
            </div>

            <!-- Props Tab -->
            <div v-if="selectedTab === 'props'">
              <PropsTab
                :isEditable="isEditable"
                :propsList="currentComponent.props"
                @set="setProps"
              />
            </div>

            <!-- Constant Data Tab -->
            <div v-if="selectedTab === 'constant_data'">
              <ConstantDataTab
                :isEditable="isEditable"
                :constants="constantData"
                :previousStates="previousStates.data || {}"
                :isReverting="isReverting ? (name) => isReverting('data', name) : null"
                @add="addNewConstantData"
                @update:item="updateConstantData"
                @remove:item="removeConstantData"
                @revert="(name) => $emit('revertState', 'data', name)"
              />
            </div>

            <!-- Methods Tab -->
            <div v-if="selectedTab === 'methods'">
              <MethodsTab
                :isEditable="isEditable"
                :methods="currentComponent.methods"
                :previousStates="previousStates.method || {}"
                :isReverting="isReverting ? (name) => isReverting('method', name) : null"
                @add="$emit('addMethod', $event)"
                @update="$emit('updateMethod', $event)"
                @remove="$emit('removeMethod', $event)"
                @revert="(name) => $emit('revertState', 'method', name)"
              />
            </div>
            <div v-if="selectedTab === 'computed'">
              <ReactivityTab
                :isEditable="isEditable"
                :reactivity="currentComponent.computed"
                reactivityName="Computed"
                :previousStates="previousStates.computed || {}"
                :isReverting="isReverting ? (name) => isReverting('computed', name) : null"
                @add="$emit('addComputed', $event)"
                @update="$emit('updateComputed', $event)"
                @remove="$emit('removeComputed', $event)"
                @revert="(name) => $emit('revertState', 'computed', name)"
              />
            </div>
            <div v-if="selectedTab === 'watch'">
              <ReactivityTab
                :isEditable="isEditable"
                :reactivity="currentComponent.watch"
                reactivityName="Watch"
                :previousStates="previousStates.watch || {}"
                :isReverting="isReverting ? (name) => isReverting('watch', name) : null"
                @add="$emit('addWatch', $event)"
                @update="$emit('updateWatch', $event)"
                @remove="$emit('removeWatch', $event)"
                @revert="(name) => $emit('revertState', 'watch', name)"
              />
            </div>

            <!-- Remote Data Component -->
            <div v-if="selectedTab === 'remote_data'">
              <RemoteData
                :isEditable="isEditable"
                :remoteData="remoteData"
                @update="updateItem"
                @add="addNewRemoteData"
                @remove:item="removeRemoteData"
              />
            </div>

            <div v-if="selectedTab === 'global_versions'">
              <div class="flex items-start justify-start space-x-2 pt-3 px-3">
                <div
                  v-if="versionInfo?.versions?.length > 0"
                  class="flex items-center justify-start space-x-4 w-full"
                >
                  <label for="version">Version:</label>
                  <select
                    v-model="selectedVersion"
                    class="border border-gray-300 rounded-md custom-focus px-2 py-1"
                  >
                    <option
                      v-for="version in versionInfo.versions"
                      :key="version._id"
                      :value="version.version"
                    >
                      <span>{{ version.version }}</span>
                      <span v-if="version.version === versionInfo.versionModel.current_version">&nbsp;&nbsp;current</span>
                      <span v-if="version.version === versionInfo.versionModel.published_version">&nbsp;&nbsp;published</span>
                    </option>
                  </select>
                  <button
                    v-if="isComponentView && selectedVersion &&
                      selectedVersion !== versionInfo.versionModel.published_version"
                    class="self-settings-add-btn"
                    @click="publishThisVersion"
                  >
                    Publish This Version
                  </button>
                  <button
                    v-if="isComponentView && selectedVersion &&
                      selectedVersion !== versionInfo.versionModel.current_version"
                    class="self-settings-add-btn"
                    @click="makeCurrentVersion"
                  >
                    Make This Version Current
                  </button>
                  <button
                    v-if="isComponentView && selectedVersion &&
                      selectedVersion !== versionInfo.versionModel.current_version &&
                      selectedVersion !== versionInfo.versionModel.published_version"
                    class="bg-red-500 text-white px-3 py-1 rounded-full"
                    @click="deleteThisVersion"
                  >
                    Delete This Version
                  </button>
                </div>
                <div v-else>
                  No versions found
                </div>
              </div>
              <div v-if="isComponentView" class="flex items-start justify-start space-x-2 pt-3 px-3">
                <button
                  class="self-settings-add-btn"
                  @click="createNewVersion"
                >
                  Create New Version
                </button>
              </div>
            </div>

            <div v-if="selectedTab === 'make_global'">
              <div class="flex items-start justify-start space-x-2 pt-3">
                <button class="self-settings-add-btn w-64 mx-auto" @click="makeGlobal">
                  Make this component as Global
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Drag bar for resizing height -->
      <div
        v-if="isEditable"
        class="resize-bar"
        @mousedown="startHeightResizing"
      >
        <img src="@/assets/vertical-resizer.svg" alt="Resize Handle" />
      </div>
    </div>
  </div>
</template>

<script>
import RemoteData from "@/components/RemoteData.vue";
import ComponentSettings from "@/components/ComponentSettings.vue";
import PropsTab from "@/components/PropsTab.vue";
import MethodsTab from "@/components/MethodsTab.vue";
import ConstantDataTab from "@/components/ConstantDataTab.vue";
import ReactivityTab from "./ReactivityTab.vue";
import CustomTooltip from "@/utilities/CustomTooltip.vue";
import * as compService from "../service/component"

export default {
  name: "SiteSelfSetting",
  props: {
    currentComponent: {
      type: Object,
      default: () => ({})
    },
    isComponentView: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    previousStates: {
      type: Object,
      default: () => ({})
    },
    isReverting: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      versionInfo: {},
      selectedVersion: null,
      defaultSettingsTabs: [
        {
          name: "Component Settings",
          value: "component_settings",
          tooltip: "Manage the component's name and toggle its global availability.  Making a component global allows it to be reused across other sites",
        },
        {
          name: "Props",
          value: "props",
          tooltip: "Props establish a one-way data flow where the parent component passes data to the child component. The child cannot modify the prop's value directly, ensuring data integrity.",
        },
        {
          name: "Methods",
          value: "methods",
          tooltip: "Methods define the actions or functions that can be performed within this component. They handle user interactions and other behaviors.",
        },
        {
          name: "Computed",
          value: "computed",
          tooltip: "Computed properties are reactive values derived from the component's state or props. They are cached and updated automatically when dependencies change.",
        },
        {
          name: "Watch",
          value: "watch",
          tooltip: "Watch properties are reactive values that are updated when a specific property changes. They are cached and updated automatically when dependencies change.",
        },
        {
          name: "Constant Data",
          value: "constant_data",
          tooltip: "Constant data consists of data properties defined in the component that can be used to store and manage the component's state.",
        },
        {
          name: "Remote Data",
          value: "remote_data",
          tooltip: "Remote data refers to data properties whose values are dynamically set based on API responses.",
        },
      ],
      selectedTab: "component_settings",
      componentName: "app",
      componentProps: ["", ""],
      height: 300,
      isResizing: false,
      maxHeight: 640,
    };
  },
  components: {
    RemoteData,
    ComponentSettings,
    PropsTab,
    MethodsTab,
    ReactivityTab,
    ConstantDataTab,
    CustomTooltip,
  },
  watch: {
    currentComponent() {
      this.loadComponentVersions()
    },
    selectedVersion() {
      if (this.isComponentView) {
        this.$router.push({
          name: 'ComponentEdit',
          params: {
            id: this.currentComponent.versionModel
          },
          query: this.selectedVersion ? {
            version: this.selectedVersion
          } : undefined
        })
      } else {
        this.$emit('selectVersion', this.selectedVersion)
      }
    }
  },
  computed: {
    settingsTabs () {
      const extra = []
      if (this.currentComponent.is_global) {
        extra.push({
          name: "Versions",
          value: "global_versions",
          tooltip: "Manage the component's versions.",
        })
      } else {
        if (this.currentComponent.name !== 'app') {
          extra.push({
            name: "Make Global",
            value: "make_global",
            tooltip: "Make this component global.",
          })
        }
      }
      return this.defaultSettingsTabs.concat(extra);
    },
    constantData() {
      return {
        label: "Const Data Source",
        type: "const",
        data: this.currentComponent.data
          .map((d, idx) => ({ ...d, idx }))
          .filter((item) => item.type === "const"),
        helpText: "Regular variable used within component.",
      };
    },
    remoteData() {
      return {
        label: "Remote Data Source",
        type: "remote",
        data: this.currentComponent.data
          .map((d, idx) => ({ ...d, idx }))
          .filter((item) => item.type === "remote"),
        helpText: "Remote data source fetched from remote api endpoint.",
      };
    },
  },
  mounted () {
    this.loadComponentVersions()
  },
  methods: {
    startHeightResizing(event) {
      this.isResizing = true;
      this.initialMouseY = event.clientY;
      this.initialHeight = this.height;
      window.addEventListener("mousemove", this.resizeHeight);
      window.addEventListener("mouseup", this.stopHeightResizing);
    },
    resizeHeight(event) {
      if (this.isResizing) {
        const deltaY = event.clientY - this.initialMouseY;
        const newHeight = this.initialHeight + deltaY;

        // Check against min and max height
        this.height = Math.max(100, Math.min(newHeight, this.maxHeight)); // Minimum 100px and maximum maxHeight
      }
    },
    stopHeightResizing() {
      this.isResizing = false;
      window.removeEventListener("mousemove", this.resizeHeight);
      window.removeEventListener("mouseup", this.stopHeightResizing);
    },
    updateConstantData(newConstantData) {
      this.$emit("update", { idx: newConstantData.idx, data: newConstantData });
    },
    removeConstantData(idx) {
      this.$emit("remove", idx);
    },
    setSettings(newSettings) {
      this.$emit("update:settings", newSettings);
    },
    setProps(newProps) {
      this.$emit("set", newProps);
    },
    addNewConstantData({ name, type }) {
      // emit to parent
      this.$emit("add", {
        name,
        type,
      });
    },
    addNewRemoteData({ name, type }) {
      // emit to parent
      this.$emit("add", {
        name,
        type,
      });
    },
    removeRemoteData(item) {
      this.$emit("remove", item.idx);
    },
    updateItem(updatedItem) {
      this.$emit("update", { idx: updatedItem.idx, data: updatedItem.data });
    },
    makeGlobal() {
      this.setSettings({ is_global: true });
      this.selectedTab = 'component_settings';
    },
    loadComponentVersions() {
      this.selectedVersion = this.currentComponent.version;
      compService.getComponentVersions(this.currentComponent._id).then(res => {
        this.versionInfo = res
      })
      if (this.selectedTab === 'global_versions' || this.selectedTab === 'make_global') {
        this.selectedTab = 'component_settings';
      }
    },
    publishThisVersion() {
      this.setSettings({ published_version: this.selectedVersion });
      this.versionInfo.versionModel.published_version = this.selectedVersion
    },
    makeCurrentVersion() {
      this.setSettings({ current_version: this.selectedVersion });
      this.versionInfo.versionModel.current_version = this.selectedVersion
    },
    async createNewVersion() {
      const newVersion = window.prompt(
        `Input new version number. larger than ${this.currentComponent.version}`
      );
      if (!newVersion) return;
      await compService.createComponent({
        component: {
          ...this.currentComponent,
          version: newVersion,
          _id: null
        },
        versionModel: this.currentComponent.versionModel
      })
      this.loadComponentVersions()
      this.$router.push({
        name: 'ComponentEdit',
        params: {
          id: this.currentComponent.versionModel
        },
        query: {
          version: newVersion
        }
      })
    },
    deleteThisVersion() {
      this.selectedTab = 'component_settings';
    },
  },
};
</script>

<style scoped>
.site-self-settings {
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  /* padding: 15px 15px 0px 15px !important; */
  background: #f8f8ff;
}

.bg-white {
  flex: 1 !important;
  overflow: auto !important;
}

.resize-bar {
  width: calc(100% - 30px) !important;
  height: 5px !important;
  background-color: #00aeef !important;
  cursor: row-resize !important;
  position: absolute !important;
  bottom: 0 !important;
  left: 15px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.resize-bar img {
  width: 20px !important;
  height: 20px !important;
  position: relative !important;
  pointer-events: none !important;
}

.selected-tab-item {
  color: #00aeef !important;
  border-color: #00aeef;
}
</style>
